import React, { useCallback, useEffect, useState } from "react";
import { DA_CartData, DA_CartDataApi } from "@danishagro/shared/src/interfaces/cartData.interface";
import { DA_CartOrderLine } from "@danishagro/shared/src/interfaces/cartOrderLine.interface";
import { FetchOptions } from "@danishagro/shared/src/interfaces/fetchOptions.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_CartPriceData } from "@danishagro/shared/src/interfaces/price.interface";
import { BasketArgs, CompleteOrder } from "../../interfaces/basket.interface";
import { useBasketRequest } from "./hooks/useBasketRequest.hook";
import { mapToCartData, mapToCartLineApi, mapToOrder } from "./helpers/basket.helper";

interface BasketHook {
    basketId?: string;
    secret?: string;
    orderSecret?: string;
    priceData: DA_CartPriceData | undefined;
    shippingDetails: DA_CartData["shippingDetails"];
    orderLines: DA_CartOrderLine[];
    addToBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_CartData>;
    updateBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_CartData>;
    removeFromBasket: (properties: BasketArgs, options?: FetchOptions) => Promise<DA_CartData>;
    resetBasket: () => void;
    getCompletedOrder: (
        orderId: string,
        orderSecret: string,
        options?: FetchOptions
    ) => Promise<CompleteOrder>;
    isLoadingBasket: boolean;
    isUpdatingBasket: boolean;
}

const BasketContext = React.createContext<BasketHook>({} as BasketHook);

type Props = {
    orderSecret?: string;
    children: React.ReactNode;
};

export const BasketProvider = ({ orderSecret: givenOrderSecret, children }: Props): JSX.Element => {
    const [basketId, setBasketId] = useState<string>();
    const [basketSecret, setBasketSecret] = useState<string>();
    const [orderSecret, _setOrderSecret] = useState<string>(givenOrderSecret);
    const [isLoadingBasket, setIsLoadingBasket] = useState(false);
    const [isUpdatingBasket, setIsUpdatingBasket] = useState(false);
    const [orderLines, setOrderLines] = useState<DA_CartOrderLine[]>([]);

    const [shippingDetails] = useState<DA_CartData["shippingDetails"]>();
    const [priceData] = useState<DA_CartPriceData>(undefined);

    const { isAppReady } = useAppData();
    const b2cRequest = useBasketRequest();

    const storeBasketSecret = useCallback(
        (secret: string) => {
            if (basketSecret !== secret) {
                localStorage.setItem("basketSecret", secret);
                setBasketSecret(secret);
            }
        },
        [basketSecret]
    );

    const storeBasketId = useCallback(
        (id: string) => {
            if (basketId !== id) {
                localStorage.setItem("basketId", id);
                setBasketId(id);
            }
        },
        [basketId]
    );

    const fetchAnonymousBasket = useCallback(
        (basketId: string, basketSecret: string): Promise<DA_CartDataApi> => {
            return b2cRequest
                .get(`/api/basket/${basketId}/`, basketSecret)
                .then((response) => response.json());
        },
        [b2cRequest]
    );

    const fetchAuthBasket = useCallback(async (): Promise<DA_CartDataApi> => {
        return b2cRequest.get("/api/basket/").then((response) => response.json());
    }, [b2cRequest]);

    const updateBasketData = useCallback((cartData: Omit<DA_CartData, "price">) => {
        if (cartData) {
            // storeBasketId(cartData.id);
            // storeBasketSecret(cartData.secret);
            setBasketId(cartData.id);
            setBasketSecret(cartData.secret);
            setOrderLines(cartData.orderLines);
            // storeCartId(cartData.id);
            // storeBasketSecret(cartData.secret);
        }
    }, []);

    const addToBasket: BasketHook["addToBasket"] = useCallback(
        async (properties, options) => {
            const cartLine = mapToCartLineApi(properties);

            setIsUpdatingBasket(true);

            try {
                let localBasketId = basketId;
                let localBasketSecret = basketSecret;

                // Create a new basket if no basket exists
                if (!basketId || !basketSecret) {
                    const newBasket = await b2cRequest
                        .get("/api/basket/new/")
                        .then((response) => response.json());

                    // TODO: Verify camelCase on newBasket
                    localBasketId = newBasket.id;
                    localBasketSecret = newBasket.secret;

                    // Store the new basket details
                    storeBasketId(localBasketId);
                    storeBasketSecret(localBasketSecret);
                }

                // Add the item to the basket
                if (localBasketId && localBasketSecret) {
                    const response = await b2cRequest.post(
                        `/api/basket/${localBasketId}/lines/`,
                        localBasketSecret,
                        cartLine,
                        options
                    );
                    const data = await response.json();

                    if (data && data.id) {
                        updateBasketData(mapToCartData(data));
                        return data;
                    } else {
                        throw new Error("Failed to add item to basket");
                    }
                }
            } catch (error) {
                console.error("Error adding to basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [b2cRequest, basketId, basketSecret, storeBasketId, storeBasketSecret, updateBasketData]
    );

    const resetBasket: BasketHook["resetBasket"] = useCallback(() => {
        localStorage.removeItem("basketId");
        localStorage.removeItem("basketSecret");
        setBasketId(undefined);
        setBasketSecret(undefined);
        setOrderLines([]);
        setIsLoadingBasket(false);
    }, []);

    const updateBasket: BasketHook["updateBasket"] = useCallback(
        async (properties, options) => {
            setIsUpdatingBasket(true);

            try {
                const response = await b2cRequest.patch(
                    `/api/basket/${basketId}/lines/${properties["id"]}/updatequantity`,
                    basketSecret,
                    properties.quantity?.toString(),
                    options
                );

                const data = await response.json();

                if (data) {
                    // Update the basket data in the state
                    updateBasketData(mapToCartData(data));
                    return data;
                } else {
                    throw new Error("Failed to update basket");
                }
            } catch (error) {
                console.error("Error updating basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [b2cRequest, basketId, basketSecret, updateBasketData]
    );

    const removeFromBasket: BasketHook["removeFromBasket"] = useCallback(
        async (properties, options) => {
            setIsUpdatingBasket(true);

            try {
                const lineId = properties["id"];

                // Make the API call to remove the item from the basket
                const response = await b2cRequest.delete(
                    `/api/basket/${basketId}/lines/${lineId}`,
                    basketSecret,
                    {},
                    options
                );

                const data = await response.json();
                if (data && data.id) {
                    // Update the basket data in the state
                    updateBasketData(mapToCartData(data));
                    return data;
                } else {
                    throw new Error("Failed to remove item from basket");
                }
            } catch (error) {
                console.error("Error removing item from basket:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [b2cRequest, basketId, basketSecret, updateBasketData]
    );

    const getCompletedOrder: BasketHook["getCompletedOrder"] = useCallback(
        async (orderId, orderSecret) => {
            setIsUpdatingBasket(true);

            try {
                const response = await fetchAnonymousBasket(orderId, orderSecret);
                return mapToOrder(response);
            } catch (error) {
                console.error("Error fetching completed order:", error);
                throw error; // Re-throw the error to allow the caller to handle it
            } finally {
                setIsUpdatingBasket(false);
            }
        },
        [fetchAnonymousBasket]
    );

    useEffect(() => {
        const localBasketId = localStorage.getItem("basketId");
        const localsecret = localStorage.getItem("basketSecret");
        const isAuthenticated = globalThis.isAuthenticated;

        // Only load or create basket if the app is ready and the user is authenticated or has a local basket
        if (isAppReady && (isAuthenticated || (localBasketId && localsecret))) {
            if (isAuthenticated) {
                fetchAuthBasket().then((basket) => {
                    updateBasketData(mapToCartData(basket));
                });
            } else {
                fetchAnonymousBasket(localBasketId, localsecret).then((basket) => {
                    updateBasketData(mapToCartData(basket));
                });
            }
        }
    }, [fetchAnonymousBasket, fetchAuthBasket, isAppReady, updateBasketData]);

    return (
        <BasketContext.Provider
            value={{
                basketId,
                secret: basketSecret,
                orderSecret,
                orderLines: orderLines,
                shippingDetails,
                resetBasket,
                addToBasket,
                updateBasket,
                removeFromBasket,
                getCompletedOrder,
                isLoadingBasket,
                isUpdatingBasket,
                priceData,
            }}
        >
            {children}
        </BasketContext.Provider>
    );
};

export const useBasket = (): BasketHook => React.useContext(BasketContext);
